import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Grid, Button } from "@mui/material";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(weekday);
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = "America/St_Johns";

const generateCalendarMatrix = (month) => {
  const start = month.startOf("month").startOf("week");
  const end = month.endOf("month").endOf("week");

  const calendar = [];
  let current = start.clone();

  while (current.isBefore(end)) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(current.clone());
      current = current.add(1, "day");
    }
    calendar.push(week);
  }

  return calendar;
};

const CustomCalendarWithSlots = ({
  selectedDate,
  onDateSelect,
  availableDates = [], // backend sends ISO strings (stored in UTC)
  selectedSlot,
  onSlotSelect,
  fetchTimeSlots,
}) => {
  const [calendarMonth, setCalendarMonth] = useState(
    dayjs().tz(TIMEZONE).startOf("month")
  );
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const matrix = generateCalendarMatrix(calendarMonth);

  const availableSet = useMemo(
    () => new Set(availableDates.map((d) => dayjs.utc(d).format("YYYY-MM-DD"))),
    [availableDates]
  );

  const isAvailable = (date) =>
    availableSet.has(date.utc().format("YYYY-MM-DD"));

  const isToday = (date) => date.isSame(dayjs().tz(TIMEZONE), "day");

  const isSelected = (date) =>
    selectedDate && date.isSame(dayjs(selectedDate).tz(TIMEZONE), "day");

  const handleMonthChange = (direction) => {
    setCalendarMonth((prev) =>
      direction === "prev" ? prev.subtract(1, "month") : prev.add(1, "month")
    );
  };

  const handleDateClick = (date) => {
    const dateStr = date.utc().format("YYYY-MM-DD");
    if (!isAvailable(date)) return;

    onDateSelect(dateStr);
    setLoadingSlots(true);
    fetchTimeSlots(dateStr)
      .then((res) => setSlots(res || []))
      .finally(() => setLoadingSlots(false));
  };

  useEffect(() => {
    if (selectedDate) {
      fetchTimeSlots(selectedDate).then((res) => setSlots(res || []));
    }
  }, [selectedDate]);

  return (
    <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={4}>
      {/* Calendar */}
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}>
          <Button onClick={() => handleMonthChange("prev")}>‹</Button>
          <Typography fontWeight="bold">
            {calendarMonth.format("MMMM YYYY")}
          </Typography>
          <Button onClick={() => handleMonthChange("next")}>›</Button>
        </Box>

        {/* Weekday headers */}
        <Grid container spacing={1} mb={1}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <Grid item xs={12 / 7} key={day}>
              <Typography
                variant="subtitle2"
                align="center"
                sx={{ color: "#999", fontWeight: 500 }}>
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Date grid */}
        {matrix.map((week, wi) => (
          <Grid container spacing={1} key={wi} mb={0.5}>
            {week.map((date, di) => {
              const disabled =
                !isAvailable(date) || !date.isSame(calendarMonth, "month");
              const selected = isSelected(date);

              return (
                <Grid item xs={12 / 7} key={di}>
                  <Box
                    onClick={() => handleDateClick(date)}
                    sx={{
                      mx: "auto",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: selected
                        ? "#FF6F61"
                        : isToday(date)
                        ? "#f5f5f5"
                        : disabled
                        ? "transparent"
                        : "#fff",
                      color: selected ? "#fff" : disabled ? "#ccc" : "#000",
                      textAlign: "center",
                      lineHeight: "40px",
                      cursor: disabled ? "default" : "pointer",
                      transition: "0.2s",
                      fontWeight: selected ? 600 : 400,
                      boxShadow: selected ? 3 : "none",
                      "&:hover": {
                        backgroundColor:
                          !disabled && !selected ? "#fce9e7" : undefined,
                      },
                    }}>
                    {date.date()}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ))}
      </Box>

      {/* Time slots */}
      <Box flex={1}>
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
          Time Slots
        </Typography>

        <Box
          display="flex"
          flexWrap="wrap"
          gap={1}
          minHeight={100}
          alignItems="flex-start">
          {loadingSlots ? (
            <Typography variant="body2" color="text.secondary">
              Loading slots...
            </Typography>
          ) : slots.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No slots available for this day.
            </Typography>
          ) : (
            slots.map((slot) => (
              <Box
                key={slot}
                onClick={() => onSlotSelect(slot)}
                sx={{
                  px: 2,
                  py: 1,
                  border: "1px solid",
                  borderColor:
                    selectedSlot === slot ? "#FF6F61" : "rgba(0, 0, 0, 0.2)",
                  borderRadius: 999,
                  backgroundColor: selectedSlot === slot ? "#FF6F61" : "#fff",
                  color: selectedSlot === slot ? "#fff" : "#333",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "0.2s",
                  "&:hover": {
                    borderColor: "#FF6F61",
                  },
                }}>
                {slot}
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomCalendarWithSlots;
